import { t } from '@/lib/i18n';
import { useCallback, useState } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/http/http-api';
import { queryKeys } from '@/lib/query-keys';

export const captchaProviders = [
  'cloudflare-turnstile',
  'google-recaptcha',
  'hcaptcha',
  'captchafox',
] as const;
export type CaptchaProvider = (typeof captchaProviders)[number];
export type CaptchaConfiguration =
  | { readonly enabled: false }
  | {
      readonly enabled: true;
      readonly provider: CaptchaProvider;
      readonly siteKey: string;
    };

/** Unlike optional social buttons, malformed or unavailable CAPTCHA settings fail closed. */
export const parseCaptchaConfiguration = (
  value: unknown,
): CaptchaConfiguration => {
  const config = value as {
    enabled?: unknown;
    provider?: unknown;
    siteKey?: unknown;
  } | null;
  if (config?.enabled === false) return { enabled: false };
  if (
    config?.enabled === true &&
    captchaProviders.includes(config.provider as CaptchaProvider) &&
    typeof config.siteKey === 'string' &&
    config.siteKey.trim() !== ''
  ) {
    return {
      enabled: true,
      provider: config.provider as CaptchaProvider,
      siteKey: config.siteKey,
    };
  }
  throw new Error(t('验证码配置不完整，请联系管理员'));
};

export const authCaptchaQueryOptions = queryOptions({
  queryKey: queryKeys.authCaptcha,
  queryFn: async ({ signal }) =>
    parseCaptchaConfiguration(
      await apiRequest<unknown>('/auth/captcha', { signal }),
    ),
  staleTime: 5 * 60_000,
  retry: false,
});

export const useCaptcha = () => {
  const query = useQuery(authCaptchaQueryOptions);
  const [verification, setVerification] = useState<{
    token: string;
    widgetKey: string;
  }>();
  const [generation, setGeneration] = useState(0);
  const widgetKey = query.data?.enabled
    ? `${query.data.provider}:${query.data.siteKey}:${generation}`
    : undefined;
  const onToken = useCallback(
    (token?: string) => {
      setVerification(token && widgetKey ? { token, widgetKey } : undefined);
    },
    [widgetKey],
  );
  const token =
    verification?.widgetKey === widgetKey ? verification?.token : undefined;
  const ready =
    !query.isError &&
    query.data !== undefined &&
    (!query.data.enabled || Boolean(token));
  return {
    query,
    onToken,
    generation,
    ready,
    getToken: () => {
      if (!ready) throw new Error(t('请先完成人机验证'));
      return query.data?.enabled ? token : undefined;
    },
    reset: () => {
      setVerification(undefined);
      setGeneration((previous) => previous + 1);
    },
  };
};

/** Better Auth consumes fetchOptions as transport options, never as request-body fields. */
export const captchaRequestOptions = (token?: string) =>
  token ? { fetchOptions: { headers: { 'x-captcha-response': token } } } : {};
